import { axiosInstance } from "@retro-ia/shared/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export interface DocumentoArchivo {
  llave: string;
  nombre: string;
  url?: string;
  size?: number;
}

export interface Documento {
  id: number;
  idPrograma: number;
  archivo: DocumentoArchivo;
  cargado: boolean;
  mensaje: string;
  creadoEl: string;
  actualizadoEl: string;
  creadoPor: string;
  actualizadoPor: string;
}

export interface DocumentoCreatePayload {
  idPrograma: number;
  archivo: DocumentoArchivo;
  cargado?: boolean;
  mensaje?: string;
}

export interface DocumentoUpdatePayload {
  id: number;
  archivo?: DocumentoArchivo;
  cargado?: boolean;
  mensaje?: string;
}

export function useDocumentos({
  page = 1,
  limit = 10,
  search = "",
  idPrograma,
}: {
  page?: number;
  limit?: number;
  search?: string;
  idPrograma?: number;
} = {}) {
  return useQuery<{
    data: Documento[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ["documentos", page, limit, search, idPrograma],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit };
      if (search) params.search = search;
      if (idPrograma) params.idPrograma = idPrograma;
      const { data } = await axiosInstance.get("/documentos", { params });
      return data;
    },

  });
}

export function useCreateDocumento(options?: { queryKey?: string[] }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DocumentoCreatePayload) => {
      const res = await axiosInstance.post("/documentos", data);
      return res.data;
    },
    onSuccess: () => {
      if (options?.queryKey) {
        queryClient.invalidateQueries({ queryKey: options.queryKey });
      } else {
        queryClient.invalidateQueries({ queryKey: ["documentos"] });
      }
      toast.success("Documento creado exitosamente");
    },
    onError: () => {
      toast.error("Error al crear el documento");
    },
  });
}

export function useUpdateDocumento() {
  return useMutation({
    mutationFn: async ({ id, ...payload }: DocumentoUpdatePayload) => {
      const { data } = await axiosInstance.patch<Documento>(
        `/documentos/${id}`,
        payload
      );
      return data;
    },
  });
}

export function useDeleteDocumento(options?: {
  queryKey?: string[];
  idPrograma?: number;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await axiosInstance.delete<Documento>(
        `/documentos/${id}`
      );
      return data;
    },
    onSuccess: () => {
      // Invalidar queries específicas si se proporcionan
      if (options?.queryKey) {
        queryClient.invalidateQueries({ queryKey: options.queryKey });
      }
      // Invalidar todas las queries de documentos
      queryClient.invalidateQueries({ queryKey: ["documentos"] });
      toast.success("Documento eliminado exitosamente");
    },
    onError: () => {
      toast.error("Error al eliminar el documento");
    },
  });
}
